/**
 * iter87.466.1 — UCL Champions Predictor, sibling of
 * `NwslPredictorModal.jsx` — simplified to a SINGLE fixture (the
 * Final): Winner (+10), Decided-by Regulation-ExtraTime-Penalties
 * (+5), Total Goals over-under a fixed 2.5 line (+5). Fixed scoring,
 * not admin-editable.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, Target, Check, Info } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";
import { SubPickLabel } from "@/components/dashboard/SubPickLabel";

const TIME_BUCKETS = [
    { key: "regulation", label: "Regulation" },
    { key: "extra_time", label: "Extra Time" },
    { key: "penalties", label: "Penalties" },
];
const GOALS_LINE = 2.5;

export default function UclChampionsPredictorModal({ open, onClose, onSaved, tournamentId, participantId, existingPick }) {
    const [draft, setDraft] = useState(existingPick || {});
    const [bracket, setBracket] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !tournamentId) return;
        setDraft(existingPick || {});
        api.getUclBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
    }, [open, tournamentId, existingPick]);

    if (!open) return null;
    const finalRound = (bracket?.rounds || []).find((r) => r.key === "final");
    const finalTie = (finalRound?.series || [])[0];
    const teamsById = {};
    (bracket?.teams || []).forEach((t) => { teamsById[t.id] = t; });
    const teamA = finalTie ? teamsById[finalTie.team_a_id] : null;
    const teamB = finalTie ? teamsById[finalTie.team_b_id] : null;
    const locked = !finalTie || finalTie.status !== "open";
    const notSetYet = !finalTie || finalTie.status === "tbd";

    const setField = (field, value) => setDraft((d) => ({ ...d, [field]: value }));
    const dirty = draft.winner_team_id !== (existingPick || {}).winner_team_id
        || draft.decided_by !== (existingPick || {}).decided_by
        || draft.goals_side !== (existingPick || {}).goals_side;

    const save = async () => {
        setSaving(true);
        try {
            await api.submitUclChampionsPredictorPick(tournamentId, participantId, draft);
            onSaved?.(draft);
            toast.success("Champions Predictor picks saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save your picks");
        } finally {
            setSaving(false);
        }
    };

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="ucl-champions-predictor-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="rounded-2xl border border-cyber/40 bg-obsidian max-w-lg w-full max-h-[90dvh] overflow-hidden flex flex-col"
        >
            <div className="flex items-center justify-between gap-2 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-white/[0.06] shrink-0">
                <div className="min-w-0 flex items-center gap-2 flex-1">
                    <div className="w-9 h-9 rounded-xl bg-cyber flex items-center justify-center shrink-0">
                        <Target className="w-4 h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display font-extrabold text-lg uppercase truncate">Champions Predictor</h3>
                </div>
                <button
                    onClick={onClose}
                    className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95 shrink-0"
                    data-testid="close-ucl-champions-predictor-modal-btn"
                    aria-label="Close"
                >
                    <ChevronRight className="w-4 h-4 rotate-90" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">

            <div className="text-[11px] text-zinc-500 mb-4 flex gap-2 items-start text-left" data-testid="ucl-champions-predictor-explainer">
                <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                    <span className="text-white font-bold uppercase tracking-tight">How it works:</span>{" "}
                    For the Final only: pick the <span className="text-cyber font-bold">Winner (+10 pts)</span>, how it's decided — Regulation / Extra Time / Penalties <span className="text-zinc-400 font-bold">(+5 pts)</span>, and whether total goals go Over or Under {GOALS_LINE} <span className="text-volt font-bold">(+5 pts)</span> <span className="text-zinc-500 italic">(shootout goals don't count)</span>. Locks at kickoff.
                </p>
            </div>

            {notSetYet && (
                <p className="text-xs text-zinc-500 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3" data-testid="ucl-champions-predictor-not-set">
                    The Final hasn't been drawn yet — check back once the Semi-finals wrap up.
                </p>
            )}

            {finalTie && !notSetYet && (
                <div
                    data-testid="ucl-champions-predictor-fixture"
                    className={`rounded-lg border p-3 ${locked ? "border-white/[0.06] bg-white/[0.015] opacity-80" : "border-white/[0.08] bg-[#141414]"}`}
                >
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 min-w-0">
                            <TeamSwatch name={teamA?.name} sport="ucl" size="w-5 h-5 text-[8px]" />
                            <span className="text-xs font-bold truncate">{teamA?.name || "TBD"}</span>
                            <span className="text-zinc-600 text-xs shrink-0">vs</span>
                            <span className="text-xs font-bold truncate">{teamB?.name || "TBD"}</span>
                            <TeamSwatch name={teamB?.name} sport="ucl" size="w-5 h-5 text-[8px]" />
                        </div>
                        {finalTie.status === "final" ? (
                            <span className="text-[10px] font-mono text-zinc-400 shrink-0">
                                FT · {finalTie.total_goals != null ? `${finalTie.total_goals} goals` : ""}{finalTie.decided_by === "penalties" ? " (PK)" : finalTie.decided_by === "extra_time" ? " (ET)" : ""}
                            </span>
                        ) : locked ? (
                            <span className="text-[9px] uppercase tracking-widest text-heat font-bold shrink-0">Locked</span>
                        ) : null}
                    </div>

                    {/* iter87.562 (user-requested) — same tiny group
                        labels as NwslPredictorModal.jsx's sibling tile. */}
                    <SubPickLabel>
                        Winner <span className="text-zinc-400 normal-case tracking-normal font-mono">+10</span>
                    </SubPickLabel>
                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                        {[teamA, teamB].filter(Boolean).map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                disabled={locked}
                                data-testid={`ucl-champions-predictor-winner-${t.id}`}
                                onClick={() => setField("winner_team_id", t.id)}
                                className={`px-2 py-1.5 rounded-lg border text-[11px] font-bold truncate disabled:cursor-not-allowed flex items-center justify-center gap-1.5 ${
                                    draft.winner_team_id === t.id ? "border-cyber bg-cyber/15 text-cyber" : "border-white/[0.08] bg-[#1a1a1a] text-zinc-300"
                                }`}
                            >
                                <TeamSwatch name={t.name} sport="ucl" size="w-5 h-5 text-[8px]" />
                                <span className="truncate min-w-0">{t.name}</span>
                            </button>
                        ))}
                    </div>

                    <SubPickLabel>
                        Game Ends In <span className="text-zinc-400 normal-case tracking-normal font-mono">+5</span>
                    </SubPickLabel>
                    <div className="grid grid-cols-3 gap-1.5 mb-2">
                        {TIME_BUCKETS.map((tb) => (
                            <button
                                key={tb.key}
                                type="button"
                                disabled={locked}
                                data-testid={`ucl-champions-predictor-time-${tb.key}`}
                                onClick={() => setField("decided_by", tb.key)}
                                className={`px-1.5 py-1.5 rounded-lg border text-[10px] font-bold disabled:cursor-not-allowed ${
                                    draft.decided_by === tb.key ? "border-volt bg-volt/15 text-volt" : "border-white/[0.08] bg-[#1a1a1a] text-zinc-300"
                                }`}
                            >
                                {tb.label}
                            </button>
                        ))}
                    </div>

                    <SubPickLabel>
                        Total Goals <span className="text-zinc-400 normal-case tracking-normal font-mono">+5</span>
                    </SubPickLabel>
                    <div className="grid grid-cols-2 gap-1.5">
                        {["over", "under"].map((side) => (
                            <button
                                key={side}
                                type="button"
                                disabled={locked}
                                data-testid={`ucl-champions-predictor-goals-${side}`}
                                onClick={() => setField("goals_side", side)}
                                className={`px-2 py-1.5 rounded-lg border text-[11px] font-bold capitalize disabled:cursor-not-allowed ${
                                    draft.goals_side === side ? "border-neon bg-neon/15 text-neon" : "border-white/[0.08] bg-[#1a1a1a] text-zinc-300"
                                }`}
                            >
                                {side} {GOALS_LINE}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            </div>

            {finalTie && !notSetYet && (
                <div className="shrink-0 glass border-t border-white/10 px-4 sm:px-6 py-3" data-testid="ucl-champions-predictor-footer">
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-[11px] uppercase tracking-widest font-bold text-zinc-400" data-testid="ucl-champions-predictor-dirty">
                            {dirty ? "Changes to save" : "No changes to save"}
                        </span>
                        <button
                            type="button"
                            disabled={saving || !dirty || locked}
                            onClick={save}
                            data-testid="ucl-champions-predictor-save-btn"
                            className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-black bg-cyber text-black font-display font-extrabold uppercase tracking-wide text-xs px-4 min-h-[44px] transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                        >
                            <Check className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Picks"}
                        </button>
                    </div>
                </div>
            )}
        </BentoModal>
    );
}
